/**
 * Internal extension registry for certificate and CSR parsing.
 *
 * Maps each supported extension OID to a definition that can decode DER,
 * encode input, and apply parsed values to the accumulator used during
 * certificate/CSR parsing.
 *
 * @module
 */

import { hexToBytes, toHex } from '#micro509/internal/asn1/asn1';
import {
	DEFAULT_MAX_DER_DEPTH,
	implicitPrimitiveContext,
	octetString,
	readRootElement,
	sequence,
} from '#micro509/internal/asn1/der';
import { OIDS } from '#micro509/internal/asn1/oids';
import type { ParsedBitFlags } from '#micro509/internal/x509/extension-bits';
import type {
	AuthorityInformationAccess,
	AuthorityInformationAccessInput,
	BasicConstraints,
	CertificatePolicies,
	DistributionPoint,
	ExtendedKeyUsage,
	InhibitAnyPolicy,
	KeyUsage,
	NameConstraints,
	ParsedNameConstraintForm,
	PolicyConstraints,
	PolicyMappings,
	SubjectAltName,
} from '#micro509/x509/extensions';
import {
	assertAuthorityInfoAccessProfile,
	assertCrlDistributionPointsProfile,
	assertExtensionCriticality,
	assertNameConstraintsProfile,
	buildSubjectKeyIdentifier,
	encodeAuthorityInfoAccess,
	encodeBasicConstraints,
	encodeCertificatePolicies,
	encodeCrlDistributionPoints,
	encodeExtendedKeyUsage,
	encodeInhibitAnyPolicy,
	encodeKeyUsage,
	encodeNameConstraints,
	encodePolicyConstraints,
	encodePolicyMappings,
	encodeSubjectAltName,
} from '#micro509/x509/extensions';
import type { ParsedDistributionPoint } from '#micro509/x509/parse';
import {
	parseAuthorityInfoAccess,
	parseAuthorityKeyIdentifier,
	parseBasicConstraints,
	parseCertificatePolicies,
	parseCrlDistributionPoints,
	parseExtendedKeyUsage,
	parseInhibitAnyPolicy,
	parseKeyUsage,
	parseNameConstraints,
	parsePolicyConstraints,
	parsePolicyMappings,
	parseSubjectAltNames,
} from '#micro509/x509/parse';

/** Whether an extension applies to certificate parsing, CSR parsing, or both. */
export type ExtensionRegistryContext = 'certificate' | 'csr';

/**
 * Readonly snapshot of decoded known extensions collected during parsing.
 *
 * Fields mirror the extension slots on {@linkcode ParsedCertificate}.
 */
export interface KnownParsedExtensionAccumulator {
	/** Basic Constraints (CA flag + optional pathLength). */
	readonly basicConstraints?: BasicConstraints;
	/** Key Usage flags (digitalSignature, keyCertSign, etc.). */
	readonly keyUsage?: ParsedBitFlags<KeyUsage>;
	/** Extended Key Usage purposes (serverAuth, clientAuth, etc.). */
	readonly extendedKeyUsage?: readonly ExtendedKeyUsage[];
	/** Subject Alternative Names (dns, ip, email, uri, srv, directoryName). */
	readonly subjectAltNames?: readonly SubjectAltName[];
	/** Issuer Alternative Names (RFC 5280 §4.2.1.7, same GeneralNames syntax). */
	readonly issuerAltNames?: readonly SubjectAltName[];
	/** Name Constraints (permitted/excluded subtrees). */
	readonly nameConstraints?: NameConstraints<ParsedNameConstraintForm>;
	/** Certificate Policies extension values. */
	readonly certificatePolicies?: CertificatePolicies;
	/** Policy Mappings from issuer to subject policy domains. */
	readonly policyMappings?: PolicyMappings;
	/** Policy Constraints (requireExplicitPolicy / inhibitPolicyMapping). */
	readonly policyConstraints?: PolicyConstraints;
	/** Inhibit Any-Policy skip-certs value. */
	readonly inhibitAnyPolicy?: InhibitAnyPolicy;
	/** Authority Information Access (OCSP, CA issuers). */
	readonly authorityInfoAccess?: readonly AuthorityInformationAccess[];
	/** CRL Distribution Points. */
	readonly crlDistributionPoints?: readonly ParsedDistributionPoint[];
	/** Hex-encoded Subject Key Identifier. */
	readonly subjectKeyIdentifier?: string;
	/** Hex-encoded Authority Key Identifier. */
	readonly authorityKeyIdentifier?: string;
}

/**
 * Mutable version of {@linkcode KnownParsedExtensionAccumulator} used during
 * extension parsing to progressively fill in decoded values.
 */
export type MutableKnownParsedExtensionAccumulator = {
	-readonly [Key in keyof KnownParsedExtensionAccumulator]: KnownParsedExtensionAccumulator[Key];
};

/**
 * Full lifecycle definition for a known X.509 extension: decode, encode,
 * and apply-to-accumulator during parsing.
 *
 * `TParsed` is the type produced when decoding DER; `TInput` is the type
 * accepted when encoding (they may differ, e.g. for NameConstraints).
 */
export interface ExtensionDefinition<TParsed, TInput = TParsed> {
	/** Dotted-decimal OID of the extension. */
	readonly oid: string;
	/** Contexts where this extension is valid (`'certificate'`, `'csr'`, or both). */
	readonly contexts: readonly ExtensionRegistryContext[];
	/** Whether the extension is marked critical by default when encoding. */
	readonly defaultCritical: boolean;
	/** When true, the extension is computed by the builder (e.g. SKI, AKI) rather than user-supplied. */
	readonly autoGenerated?: boolean;
	/** Decode the extension's DER extnValue into a typed value. */
	decode(valueDer: Uint8Array): TParsed;
	/** Encode a typed input into the extension's DER extnValue. */
	encode(value: TInput): Uint8Array;
	/**
	 * Apply the RFC 5280 profile rules this extension's encoder enforces to an
	 * already-decoded value, throwing a coded encoder error on violation.
	 *
	 * Decoding proves a payload's structure; it does not prove the payload obeys
	 * the profile, because the parser is deliberately tolerant. Builders route a
	 * `customExtensions` payload carrying a known OID through here so a raw value
	 * meets the same bar as the typed input for that extension. Implementations
	 * delegate to the encoder that owns the rule so the two cannot drift.
	 *
	 * Parsing never calls this.
	 */
	assertProfile(value: TParsed, critical: boolean): void;
	/** Store a decoded value into the parse-time accumulator. */
	applyParsed(accumulator: MutableKnownParsedExtensionAccumulator, value: TParsed): void;
}

/**
 * An {@linkcode ExtensionDefinition} after registration, carrying the
 * decode-then-validate closure captured when `TParsed` was concrete.
 */
export type RegisteredExtensionDefinition<TParsed, TInput = TParsed> = ExtensionDefinition<
	TParsed,
	TInput
> & {
	/** Decode a DER extnValue and apply this extension's profile rules to it. */
	assertDerProfile(valueDer: Uint8Array, critical: boolean): void;
};

/**
 * Module-internal decode-and-apply closures, captured at definition time.
 *
 * Each entry is keyed by OID and populated by {@linkcode defineExtensionDefinition}
 * when the concrete generic type is known, avoiding the correlated-union problem
 * that would arise from calling `decode` and `applyParsed` through a union type.
 */
const extensionAppliers = new Map<
	string,
	(accumulator: MutableKnownParsedExtensionAccumulator, valueDer: Uint8Array) => void
>();

/** Registry entry for Basic Constraints (OID 2.5.29.19). Critical by default. */
export const BASIC_CONSTRAINTS_EXTENSION_DEFINITION: RegisteredExtensionDefinition<BasicConstraints> =
	defineExtensionDefinition<BasicConstraints>({
		oid: OIDS.basicConstraints,
		contexts: ['certificate', 'csr'],
		defaultCritical: true,
		decode: (valueDer) => parseBasicConstraints(valueDer),
		encode: (value) => encodeBasicConstraints(value),
		assertProfile: (value) => {
			encodeBasicConstraints(value);
		},
		applyParsed: (accumulator, value) => {
			accumulator.basicConstraints = value;
		},
	});

/** Registry entry for Key Usage (OID 2.5.29.15). Critical by default. */
export const KEY_USAGE_EXTENSION_DEFINITION: RegisteredExtensionDefinition<
	ParsedBitFlags<KeyUsage>,
	readonly KeyUsage[]
> = defineExtensionDefinition<ParsedBitFlags<KeyUsage>, readonly KeyUsage[]>({
	oid: OIDS.keyUsage,
	contexts: ['certificate', 'csr'],
	defaultCritical: true,
	decode: (valueDer) => parseKeyUsage(valueDer),
	encode: (value) => encodeKeyUsage(value),
	assertProfile: (value) => {
		encodeKeyUsage(value.flags);
	},
	applyParsed: (accumulator, value) => {
		accumulator.keyUsage = value;
	},
});

/** Registry entry for Extended Key Usage (OID 2.5.29.37). Non-critical by default. */
export const EXTENDED_KEY_USAGE_EXTENSION_DEFINITION: RegisteredExtensionDefinition<
	readonly ExtendedKeyUsage[]
> = defineExtensionDefinition<readonly ExtendedKeyUsage[]>({
	oid: OIDS.extendedKeyUsage,
	contexts: ['certificate', 'csr'],
	defaultCritical: false,
	decode: (valueDer) => parseExtendedKeyUsage(valueDer),
	encode: (value) => encodeExtendedKeyUsage(value),
	assertProfile: (value) => {
		encodeExtendedKeyUsage(value);
	},
	applyParsed: (accumulator, value) => {
		accumulator.extendedKeyUsage = value;
	},
});

/** Registry entry for Subject Alternative Name (OID 2.5.29.17). Non-critical by default. */
export const SUBJECT_ALT_NAME_EXTENSION_DEFINITION: RegisteredExtensionDefinition<
	readonly SubjectAltName[]
> = defineExtensionDefinition<readonly SubjectAltName[]>({
	oid: OIDS.subjectAltName,
	contexts: ['certificate', 'csr'],
	defaultCritical: false,
	decode: (valueDer) => parseSubjectAltNames(valueDer),
	encode: (value) => sequence(value.map(encodeSubjectAltName)),
	assertProfile: (value) => {
		for (const name of value) {
			encodeSubjectAltName(name);
		}
	},
	applyParsed: (accumulator, value) => {
		accumulator.subjectAltNames = value;
	},
});

/** Registry entry for Issuer Alternative Name (OID 2.5.29.18). Non-critical by default. */
export const ISSUER_ALT_NAME_EXTENSION_DEFINITION: RegisteredExtensionDefinition<
	readonly SubjectAltName[]
> = defineExtensionDefinition<readonly SubjectAltName[]>({
	oid: OIDS.issuerAltName,
	contexts: ['certificate'],
	defaultCritical: false,
	decode: (valueDer) => parseSubjectAltNames(valueDer, 'issuerAltName'),
	encode: (value) => sequence(value.map(encodeSubjectAltName)),
	assertProfile: (value) => {
		for (const name of value) {
			encodeSubjectAltName(name);
		}
	},
	applyParsed: (accumulator, value) => {
		accumulator.issuerAltNames = value;
	},
});

/** Registry entry for Name Constraints (OID 2.5.29.30). Critical by default. */
export const NAME_CONSTRAINTS_EXTENSION_DEFINITION: RegisteredExtensionDefinition<
	NameConstraints<ParsedNameConstraintForm>,
	NameConstraints
> = defineExtensionDefinition<NameConstraints<ParsedNameConstraintForm>, NameConstraints>({
	oid: OIDS.nameConstraints,
	contexts: ['certificate', 'csr'],
	defaultCritical: true,
	decode: (valueDer) => parseNameConstraints(valueDer),
	encode: (value) => encodeNameConstraints(value),
	assertProfile: (value, critical) => {
		assertNameConstraintsProfile(value);
		assertExtensionCriticality('nameConstraints', true, critical);
	},
	applyParsed: (accumulator, value) => {
		accumulator.nameConstraints = value;
	},
});

/** Registry entry for Certificate Policies (OID 2.5.29.32). Non-critical by default. */
export const CERTIFICATE_POLICIES_EXTENSION_DEFINITION: RegisteredExtensionDefinition<CertificatePolicies> =
	defineExtensionDefinition<CertificatePolicies>({
		oid: OIDS.certificatePolicies,
		contexts: ['certificate', 'csr'],
		defaultCritical: false,
		decode: (valueDer) => parseCertificatePolicies(valueDer),
		encode: (value) => encodeCertificatePolicies(value),
		assertProfile: (value) => {
			encodeCertificatePolicies(value);
		},
		applyParsed: (accumulator, value) => {
			accumulator.certificatePolicies = value;
		},
	});

/** Registry entry for Policy Mappings (OID 2.5.29.33). Critical by default. */
export const POLICY_MAPPINGS_EXTENSION_DEFINITION: RegisteredExtensionDefinition<PolicyMappings> =
	defineExtensionDefinition<PolicyMappings>({
		oid: OIDS.policyMappings,
		contexts: ['certificate', 'csr'],
		defaultCritical: true,
		decode: (valueDer) => parsePolicyMappings(valueDer),
		encode: (value) => encodePolicyMappings(value),
		assertProfile: (value) => {
			encodePolicyMappings(value);
		},
		applyParsed: (accumulator, value) => {
			accumulator.policyMappings = value;
		},
	});

/** Registry entry for Policy Constraints (OID 2.5.29.36). Critical by default. */
export const POLICY_CONSTRAINTS_EXTENSION_DEFINITION: RegisteredExtensionDefinition<PolicyConstraints> =
	defineExtensionDefinition<PolicyConstraints>({
		oid: OIDS.policyConstraints,
		contexts: ['certificate', 'csr'],
		defaultCritical: true,
		decode: (valueDer) => parsePolicyConstraints(valueDer),
		encode: (value) => encodePolicyConstraints(value),
		assertProfile: (value, critical) => {
			encodePolicyConstraints(value);
			assertExtensionCriticality('policyConstraints', true, critical);
		},
		applyParsed: (accumulator, value) => {
			accumulator.policyConstraints = value;
		},
	});

/** Registry entry for Inhibit anyPolicy (OID 2.5.29.54). Critical by default. */
export const INHIBIT_ANY_POLICY_EXTENSION_DEFINITION: RegisteredExtensionDefinition<InhibitAnyPolicy> =
	defineExtensionDefinition<InhibitAnyPolicy>({
		oid: OIDS.inhibitAnyPolicy,
		contexts: ['certificate', 'csr'],
		defaultCritical: true,
		decode: (valueDer) => parseInhibitAnyPolicy(valueDer),
		encode: (value) => encodeInhibitAnyPolicy(value),
		assertProfile: (value, critical) => {
			encodeInhibitAnyPolicy(value);
			assertExtensionCriticality('inhibitAnyPolicy', true, critical);
		},
		applyParsed: (accumulator, value) => {
			accumulator.inhibitAnyPolicy = value;
		},
	});

/** Registry entry for Authority Information Access (OID 1.3.6.1.5.5.7.1.1). Non-critical. */
export const AUTHORITY_INFO_ACCESS_EXTENSION_DEFINITION: RegisteredExtensionDefinition<
	readonly AuthorityInformationAccess[],
	readonly AuthorityInformationAccessInput[]
> = defineExtensionDefinition<
	readonly AuthorityInformationAccess[],
	readonly AuthorityInformationAccessInput[]
>({
	oid: OIDS.authorityInfoAccess,
	contexts: ['certificate', 'csr'],
	defaultCritical: false,
	decode: (valueDer) => parseAuthorityInfoAccess(valueDer),
	encode: (value) => encodeAuthorityInfoAccess(value),
	assertProfile: (value, critical) => {
		assertAuthorityInfoAccessProfile(value);
		assertExtensionCriticality('authorityInfoAccess', false, critical);
	},
	applyParsed: (accumulator, value) => {
		accumulator.authorityInfoAccess = value;
	},
});

/** Registry entry for CRL Distribution Points (OID 2.5.29.31). Non-critical by default. */
export const CRL_DISTRIBUTION_POINTS_EXTENSION_DEFINITION: RegisteredExtensionDefinition<
	readonly ParsedDistributionPoint[],
	readonly DistributionPoint[]
> = defineExtensionDefinition<readonly ParsedDistributionPoint[], readonly DistributionPoint[]>({
	oid: OIDS.cRLDistributionPoints,
	contexts: ['certificate', 'csr'],
	defaultCritical: false,
	decode: (valueDer) => parseCrlDistributionPoints(valueDer),
	encode: (value) => encodeCrlDistributionPoints(value),
	assertProfile: (value) => {
		assertCrlDistributionPointsProfile(value);
	},
	applyParsed: (accumulator, value) => {
		accumulator.crlDistributionPoints = value;
	},
});

/** Registry entry for Subject Key Identifier (OID 2.5.29.14). Auto-generated; non-critical. */
export const SUBJECT_KEY_IDENTIFIER_EXTENSION_DEFINITION: RegisteredExtensionDefinition<
	string,
	string | Uint8Array
> = defineExtensionDefinition<string, string | Uint8Array>({
	oid: OIDS.subjectKeyIdentifier,
	contexts: ['certificate'],
	defaultCritical: false,
	autoGenerated: true,
	decode: (valueDer) => decodeSubjectKeyIdentifier(valueDer),
	encode: (value) => octetString(normalizeKeyIdentifier(value)),
	assertProfile: (value, critical) => {
		hexToBytes(value);
		assertExtensionCriticality('subjectKeyIdentifier', false, critical);
	},
	applyParsed: (accumulator, value) => {
		accumulator.subjectKeyIdentifier = value;
	},
});

/** Registry entry for Authority Key Identifier (OID 2.5.29.35). Auto-generated; non-critical. */
export const AUTHORITY_KEY_IDENTIFIER_EXTENSION_DEFINITION: RegisteredExtensionDefinition<
	string | undefined,
	string | Uint8Array
> = defineExtensionDefinition<string | undefined, string | Uint8Array>({
	oid: OIDS.authorityKeyIdentifier,
	contexts: ['certificate'],
	defaultCritical: false,
	autoGenerated: true,
	decode: (valueDer) => parseAuthorityKeyIdentifier(valueDer),
	encode: (value) => sequence([implicitPrimitiveContext(0, normalizeKeyIdentifier(value))]),
	assertProfile: (value, critical) => {
		if (value !== undefined) {
			hexToBytes(value);
		}
		assertExtensionCriticality('authorityKeyIdentifier', false, critical);
	},
	applyParsed: (accumulator, value) => {
		if (value !== undefined) {
			accumulator.authorityKeyIdentifier = value;
		}
	},
});

/** Union of all built-in extension definition constants. */
export type KnownExtensionDefinition =
	| typeof BASIC_CONSTRAINTS_EXTENSION_DEFINITION
	| typeof KEY_USAGE_EXTENSION_DEFINITION
	| typeof EXTENDED_KEY_USAGE_EXTENSION_DEFINITION
	| typeof SUBJECT_ALT_NAME_EXTENSION_DEFINITION
	| typeof ISSUER_ALT_NAME_EXTENSION_DEFINITION
	| typeof NAME_CONSTRAINTS_EXTENSION_DEFINITION
	| typeof CERTIFICATE_POLICIES_EXTENSION_DEFINITION
	| typeof POLICY_MAPPINGS_EXTENSION_DEFINITION
	| typeof POLICY_CONSTRAINTS_EXTENSION_DEFINITION
	| typeof INHIBIT_ANY_POLICY_EXTENSION_DEFINITION
	| typeof AUTHORITY_INFO_ACCESS_EXTENSION_DEFINITION
	| typeof CRL_DISTRIBUTION_POINTS_EXTENSION_DEFINITION
	| typeof SUBJECT_KEY_IDENTIFIER_EXTENSION_DEFINITION
	| typeof AUTHORITY_KEY_IDENTIFIER_EXTENSION_DEFINITION;

/** All built-in extension definitions, in registration order. */
export const CERT_CSR_EXTENSION_DEFINITIONS: readonly KnownExtensionDefinition[] = [
	BASIC_CONSTRAINTS_EXTENSION_DEFINITION,
	KEY_USAGE_EXTENSION_DEFINITION,
	EXTENDED_KEY_USAGE_EXTENSION_DEFINITION,
	SUBJECT_ALT_NAME_EXTENSION_DEFINITION,
	ISSUER_ALT_NAME_EXTENSION_DEFINITION,
	NAME_CONSTRAINTS_EXTENSION_DEFINITION,
	CERTIFICATE_POLICIES_EXTENSION_DEFINITION,
	POLICY_MAPPINGS_EXTENSION_DEFINITION,
	POLICY_CONSTRAINTS_EXTENSION_DEFINITION,
	INHIBIT_ANY_POLICY_EXTENSION_DEFINITION,
	AUTHORITY_INFO_ACCESS_EXTENSION_DEFINITION,
	CRL_DISTRIBUTION_POINTS_EXTENSION_DEFINITION,
	SUBJECT_KEY_IDENTIFIER_EXTENSION_DEFINITION,
	AUTHORITY_KEY_IDENTIFIER_EXTENSION_DEFINITION,
];

/** OID-keyed lookup for known extension definitions. */
const EXTENSION_DEFINITION_MAP = new Map<string, KnownExtensionDefinition>(
	CERT_CSR_EXTENSION_DEFINITIONS.map((definition) => [definition.oid, definition]),
);

/**
 * Return all known extension definitions, optionally filtered by context.
 *
 * @param context If provided, only definitions valid in that context are returned.
 */
export function listExtensionDefinitions(
	context?: ExtensionRegistryContext,
): readonly KnownExtensionDefinition[] {
	if (context === undefined) {
		return CERT_CSR_EXTENSION_DEFINITIONS;
	}
	return CERT_CSR_EXTENSION_DEFINITIONS.filter((definition) =>
		definition.contexts.includes(context),
	);
}

/**
 * Look up a known extension definition by OID.
 *
 * Overloaded for each built-in OID constant to return the exact definition type;
 * the generic `string` form returns the union or `undefined`.
 *
 * @param oid Dotted-decimal extension OID.
 * @returns The matching definition, or `undefined` for unrecognized OIDs.
 */
export function getExtensionDefinition(
	oid: typeof OIDS.basicConstraints,
): typeof BASIC_CONSTRAINTS_EXTENSION_DEFINITION;
/** Exact definition for the Key Usage extension. */
export function getExtensionDefinition(
	oid: typeof OIDS.keyUsage,
): typeof KEY_USAGE_EXTENSION_DEFINITION;
/** Exact definition for the Extended Key Usage extension. */
export function getExtensionDefinition(
	oid: typeof OIDS.extendedKeyUsage,
): typeof EXTENDED_KEY_USAGE_EXTENSION_DEFINITION;
/** Exact definition for the Subject Alternative Name extension. */
export function getExtensionDefinition(
	oid: typeof OIDS.subjectAltName,
): typeof SUBJECT_ALT_NAME_EXTENSION_DEFINITION;
/** Exact definition for the Issuer Alternative Name extension. */
export function getExtensionDefinition(
	oid: typeof OIDS.issuerAltName,
): typeof ISSUER_ALT_NAME_EXTENSION_DEFINITION;
/** Exact definition for the Name Constraints extension. */
export function getExtensionDefinition(
	oid: typeof OIDS.nameConstraints,
): typeof NAME_CONSTRAINTS_EXTENSION_DEFINITION;
/** Exact definition for the Certificate Policies extension. */
export function getExtensionDefinition(
	oid: typeof OIDS.certificatePolicies,
): typeof CERTIFICATE_POLICIES_EXTENSION_DEFINITION;
/** Exact definition for the Policy Mappings extension. */
export function getExtensionDefinition(
	oid: typeof OIDS.policyMappings,
): typeof POLICY_MAPPINGS_EXTENSION_DEFINITION;
/** Exact definition for the Policy Constraints extension. */
export function getExtensionDefinition(
	oid: typeof OIDS.policyConstraints,
): typeof POLICY_CONSTRAINTS_EXTENSION_DEFINITION;
/** Exact definition for the Inhibit Any-Policy extension. */
export function getExtensionDefinition(
	oid: typeof OIDS.inhibitAnyPolicy,
): typeof INHIBIT_ANY_POLICY_EXTENSION_DEFINITION;
/** Exact definition for the Authority Information Access extension. */
export function getExtensionDefinition(
	oid: typeof OIDS.authorityInfoAccess,
): typeof AUTHORITY_INFO_ACCESS_EXTENSION_DEFINITION;
/** Exact definition for the CRL Distribution Points extension. */
export function getExtensionDefinition(
	oid: typeof OIDS.cRLDistributionPoints,
): typeof CRL_DISTRIBUTION_POINTS_EXTENSION_DEFINITION;
/** Exact definition for the Subject Key Identifier extension. */
export function getExtensionDefinition(
	oid: typeof OIDS.subjectKeyIdentifier,
): typeof SUBJECT_KEY_IDENTIFIER_EXTENSION_DEFINITION;
/** Exact definition for the Authority Key Identifier extension. */
export function getExtensionDefinition(
	oid: typeof OIDS.authorityKeyIdentifier,
): typeof AUTHORITY_KEY_IDENTIFIER_EXTENSION_DEFINITION;
/** Generic lookup: returns the matching definition, or `undefined` for unrecognized OIDs. */
export function getExtensionDefinition(oid: string): KnownExtensionDefinition | undefined;
export function getExtensionDefinition(oid: string): KnownExtensionDefinition | undefined {
	return EXTENSION_DEFINITION_MAP.get(oid);
}

/**
 * If `oid` matches a known extension valid in `context`, decode its DER
 * value and store the result in the accumulator.
 *
 * @returns `true` if the extension was recognized and applied.
 */
export function decodeAndApplyKnownExtension(
	context: ExtensionRegistryContext,
	oid: string,
	accumulator: MutableKnownParsedExtensionAccumulator,
	valueDer: Uint8Array,
): boolean {
	const definition = getExtensionDefinition(oid);
	if (definition === undefined || !definition.contexts.includes(context)) {
		return false;
	}
	const applier = extensionAppliers.get(oid);
	if (applier === undefined) {
		return false;
	}
	applier(accumulator, valueDer);
	return true;
}

/**
 * Compute a Subject Key Identifier (SHA-1 of the public key bits) from
 * a DER-encoded SubjectPublicKeyInfo.
 */
export function buildSubjectKeyIdentifierFromSubjectPublicKeyInfo(
	subjectPublicKeyInfo: Uint8Array,
): Uint8Array {
	return buildSubjectKeyIdentifier(subjectPublicKeyInfo);
}

/**
 * Register an {@linkcode ExtensionDefinition} literal, capturing its
 * decode-and-apply and decode-and-validate closures.
 *
 * Both closures are built here, where `TParsed` is concrete, so that
 * `decodeAndApplyKnownExtension` and `assertDerProfile` can be called through the
 * union-typed `KnownExtensionDefinition` without hitting TypeScript's
 * correlated-union limitation.
 */
function defineExtensionDefinition<TParsed, TInput = TParsed>(
	definition: ExtensionDefinition<TParsed, TInput>,
): RegisteredExtensionDefinition<TParsed, TInput> {
	extensionAppliers.set(definition.oid, (accumulator, valueDer) => {
		definition.applyParsed(accumulator, definition.decode(valueDer));
	});
	return {
		...definition,
		assertDerProfile: (valueDer, critical) => {
			definition.assertProfile(definition.decode(valueDer), critical);
		},
	};
}

/** Accept hex string or Uint8Array and return raw bytes. */
function normalizeKeyIdentifier(value: string | Uint8Array): Uint8Array {
	return typeof value === 'string' ? hexToBytes(value) : value;
}

/** Decode an SKI extension value DER to a hex string. */
function decodeSubjectKeyIdentifier(valueDer: Uint8Array): string {
	const element = readRootElement(valueDer, { maxDepth: DEFAULT_MAX_DER_DEPTH });
	if (element.tag !== 0x04) {
		throw new Error('SubjectKeyIdentifier must be an OCTET STRING');
	}
	return toHex(element.value);
}
